import React, { useMemo, useState, useEffect, useCallback, useRef } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import BrandWheelLoader from "../BrandWheelLoader";
import { toast } from "react-toastify";

import GalleryThemePanel from "./GalleryThemePanel";
import GalleryThemePreview from "./GalleryThemePreview";
import { resolveProfileGalleryTheme, useSaveGalleryTheme } from "./useGalleryTheme";
import {
  normalizeGalleryTheme,
  isNeutralTheme,
  resolveSmallSurfaceAccents,
  ACCENT_LEGIBILITY_TOAST_COPY,
} from "./galleryTheme";
import { T } from "./constants";

const sameTheme = (a, b) =>
  JSON.stringify(normalizeGalleryTheme(a)) === JSON.stringify(normalizeGalleryTheme(b));

// The theming studio: controls panel (left) + live preview (right) + a save
// footer. Owns the DRAFT theme; only writes on Save. Handles every state:
// loading, error, first-run (neutral), editing (unsaved), saved, disabled.
const GalleryThemeStudio = ({
  userId,
  profile,
  isLoading = false,
  isError = false,
  onRetry,
  displayName,
  handle,
  inModal = false,
  // T22.576: the modal body's height. Default keeps the old fixed 72vh;
  // ThemesPopup passes a responsive value so below md the studio fills the
  // popup's flex body instead of overflowing its fixed-height frame.
  modalHeight = "72vh",
}) => {
  const serverBaseline = useMemo(
    () => resolveProfileGalleryTheme(profile, userId, true),
    [profile, userId]
  );
  const vaultTheme = profile?.vault_theme || null;

  // `baseline` is the theme we consider persisted (the point dirtiness is
  // measured from). It starts at server truth but is also advanced locally the
  // moment a save resolves — otherwise the footer keeps reading "Unsaved
  // changes" after a successful save whenever the server can't echo the field
  // back (BE gap THM-F1/F2) so `serverBaseline` never catches up to the draft
  // (BUG-gallery-6edf20 / BUG-gallery-e04b73 / QA-112).
  const [baseline, setBaseline] = useState(serverBaseline);
  const [draft, setDraft] = useState(serverBaseline);
  const { save, isSaving } = useSaveGalleryTheme(userId);

  // Adopt fresh server truth as the baseline, and re-seed the draft when the
  // user has no in-progress edits, so a background refetch never clobbers
  // unsaved work.
  useEffect(() => {
    setDraft((cur) => (sameTheme(cur, baseline) ? serverBaseline : cur));
    setBaseline((cur) => (sameTheme(cur, serverBaseline) ? cur : serverBaseline));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverBaseline]);

  const dirty = !sameTheme(draft, baseline);

  // gordon-0918c-accent-on-small-surfaces-legible-family-color-toast: fires
  // the shared legibility toast once per actual USER-DRIVEN accent change
  // (a color swatch click, custom-picker confirm, or preset/palette pick —
  // every accent-setting path in GalleryThemePanel funnels through this
  // `setTheme` prop or `setField`). Deliberately NOT wired into the
  // server-reconciliation effect above (background refetch) or onDiscard
  // below, both of which call the raw `setDraft` directly — so a refetch or
  // a discard can never fire this toast, only a real edit. Comparing against
  // a ref (not a render-time value) means this can only ever fire once per
  // distinct accent value, never once per render.
  const accentToastLastSeen = useRef(serverBaseline.accent);
  const applyUserDraftChange = useCallback((updater) => {
    setDraft((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      if (
        next?.accent &&
        next.accent !== accentToastLastSeen.current &&
        resolveSmallSurfaceAccents(next).anyAdjusted
      ) {
        toast.info(ACCENT_LEGIBILITY_TOAST_COPY);
      }
      if (next?.accent) accentToastLastSeen.current = next.accent;
      return next;
    });
  }, []);

  const setField = useCallback(
    (key, val) => applyUserDraftChange((d) => ({ ...d, [key]: val })),
    [applyUserDraftChange]
  );

  const onSave = () => {
    if (!dirty || isSaving) return;
    // Snapshot the exact theme being saved; the local persist inside `save`
    // always succeeds, so this snapshot IS the new persisted baseline whenever
    // the server round-trips the field OR only silently drops it while still
    // accepting the request (onLocalOnly — the KNOWN, benign BE-gap case).
    const snapshot = draft;
    const commit = () => setBaseline(snapshot);
    save(snapshot, {
      onSuccess: () => {
        commit();
        toast.success("Gallery theme saved!");
      },
      onLocalOnly: () => {
        commit();
        toast.info("Theme applied. Full sync will land with the next backend update.");
      },
      // BUG FIX (ISP-9999, gordon-0918-gallery-theme-save-does-not-change-
      // gallery-dev): onError means the PATCH itself genuinely failed
      // (network error, 5xx, timeout) -- NOT the same as onLocalOnly's "server
      // accepted the request but silently dropped the field". This used to
      // also `commit()`, advancing the baseline and clearing "Unsaved
      // changes" even though the server never received the theme; the ONLY
      // copy left was this device's localStorage, so a fresh load (or any
      // other device/session, or the visitor-facing public Gallery) kept
      // rendering the OLD theme with no visible sign anything was wrong --
      // "Pressing Save on Gallery Theme didn't change the Gallery at all".
      // Do NOT commit here: keep the draft dirty so the footer keeps reading
      // "Unsaved changes" and the user can retry Save.
      onError: () => {
        toast.error("Couldn't save your Gallery Theme. Check your connection and try Save again.");
      },
    });
  };

  const onDiscard = () => setDraft(baseline);

  // ---- loading ----
  // On-brand Wheel loader. Standalone => `fullpage` (always screen-centre, the
  // shared component's own viewport-centring variant). In a modal => `page`,
  // block-centred inside the dialog, which is already viewport-centred itself;
  // a fixed overlay there would cover the modal it belongs to.
  if (isLoading) {
    return (
      <Box sx={{ height: inModal ? "70vh" : undefined, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <BrandWheelLoader variant={inModal ? "page" : "fullpage"} reroll label="Loading theme…" />
      </Box>
    );
  }

  // ---- error ----
  if (isError) {
    return (
      <Box sx={{ height: inModal ? "70vh" : "calc(100vh - 64px)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "12px" }}>
        <Typography sx={{ fontSize: 16, fontWeight: 700, color: T.ink, fontFamily: T.fontBody }}>Couldn't load your Gallery</Typography>
        <Typography sx={{ fontSize: 14, color: T.muted, fontFamily: T.fontBody }}>Check your connection and try again.</Typography>
        {onRetry && (
          <Box role="button" tabIndex={0} onClick={onRetry} onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onRetry()} sx={{ mt: "6px", background: T.primary, color: "#fff", borderRadius: "8px", px: "20px", py: "8px", fontSize: 14, fontWeight: 600, fontFamily: T.fontBody, cursor: "pointer" }}>
            Retry
          </Box>
        )}
      </Box>
    );
  }

  const firstRun = isNeutralTheme(baseline);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0, height: inModal ? modalHeight : "calc(100vh - 64px)" }}>
      {/*
        BUG FIX (ISP-9999, gallery-theme-modal-clips-390): this row used to be
        an unconditional `display:"flex"` (row) with the controls panel
        `flexShrink:0` — at <600px that panel's own minWidth:300 (see
        GalleryThemePanel.jsx) can't coexist with a preview pane in the same
        row, so the preview got pushed off-screen (measured scrollWidth 674
        vs a 390 viewport — a naked 284px horizontal overflow with no visual
        affordance) and, inside either modal entry point (ThemesPopup /
        GallerySettings "Gallery Theme" tab), the modal's own
        `overflow:"hidden"` turned that overflow into real clipping of text,
        preset tiles, wallpaper tabs and the Save button. Below `sm` (600px)
        stack the panel above the preview instead of beside it; `overflowY:
        "auto"` is a safety net so a still-tall stacked column scrolls inside
        the modal's fixed height rather than being clipped by it.
      */}
      <Box sx={{ flex: 1, display: "flex", flexDirection: { xs: "column", sm: "row" }, minHeight: 0, overflowY: "auto" }}>
        {/* controls */}
        <Box
          data-testid="gt-controls-panel-wrap"
          sx={{
            flexShrink: 0,
            width: { xs: "100%", sm: "auto" },
            borderRight: { xs: "none", sm: `1px solid ${T.line}` },
            borderBottom: { xs: `1px solid ${T.line}`, sm: "none" },
            height: { xs: "auto", sm: "100%" },
          }}
        >
          <GalleryThemePanel theme={draft} setField={setField} setTheme={applyUserDraftChange} vaultTheme={vaultTheme} />
        </Box>
        {/* live preview */}
        {/* T22.576: stacked (<sm) the preview is a fixed 420px window; on a
            short phone (360x740 measured) the scroll area above the footer is
            only ~403px, so the card was always partly cut. Cap it at the
            scroll area's height so the whole Live preview fits on screen. */}
        <Box data-testid="gt-preview-pane" sx={{ flex: 1, minWidth: 0, height: { xs: 420, sm: "100%" }, maxHeight: { xs: "100%", sm: "none" } }}>
          <GalleryThemePreview theme={draft} vaultTheme={vaultTheme} displayName={displayName} handle={handle} />
        </Box>
      </Box>

      {/* footer
        BUG FIX (ISP-9999, theme-modal-390-footer-save-wraps): this row used to be
        an unconditional `display:"flex"` row with `justifyContent:"space-between"`
        and no responsive sx at all, and the two action Boxes had no
        `justifyContent`/`whiteSpace` of their own. At 390px the row has too
        little width for the status text + both buttons side by side, so
        "Save theme" wrapped onto 2 lines (and, since the actions row's
        default `alignItems` is `stretch`, "Discard" stretched to match that
        taller wrapped sibling with its own un-centred label riding high in
        the extra height) and "Unsaved changes" also wrapped. Below `sm`
        (600px) stack the status line above the actions row (full width, no
        more 3-way horizontal squeeze) and make every label `whiteSpace:
        "nowrap"` + centred (`justifyContent:"center"`) at every breakpoint
        so a label is single-line and centred regardless of the box's height.
      */}
      <Box
        data-testid="gt-footer"
        sx={{
          flexShrink: 0,
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" },
          justifyContent: "space-between",
          gap: { xs: "10px", sm: 0 },
          borderTop: `1px solid ${T.line}`,
          background: "#fff",
          px: "18px",
          py: "12px",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: dirty ? "#F59E0B" : "#22C55E", flexShrink: 0 }} />
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: dirty ? "#B45309" : "#15803D", fontFamily: T.fontBody, whiteSpace: "nowrap" }}>
            {dirty ? "Unsaved changes" : firstRun ? "No theme yet — pick a look" : "All changes saved"}
          </Typography>
        </Box>
        <Box data-testid="gt-footer-actions" sx={{ display: "flex", gap: "10px" }}>
          <Box
            data-testid="gt-discard-btn"
            role="button"
            tabIndex={dirty ? 0 : -1}
            aria-disabled={!dirty}
            onClick={onDiscard}
            onKeyDown={(e) => dirty && (e.key === "Enter" || e.key === " ") && onDiscard()}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              whiteSpace: "nowrap",
              flex: { xs: 1, sm: "initial" },
              border: `1px solid ${T.line}`,
              borderRadius: "8px",
              px: "18px",
              py: "8px",
              fontSize: 14,
              fontWeight: 600,
              fontFamily: T.fontBody,
              color: dirty ? T.ink : "#B9B9BE",
              cursor: dirty ? "pointer" : "default",
              "&:focus-visible": { outline: `2px solid ${T.primary}` },
            }}
          >
            Discard
          </Box>
          <Box
            data-testid="gt-save-btn"
            role="button"
            tabIndex={dirty && !isSaving ? 0 : -1}
            aria-disabled={!dirty || isSaving}
            onClick={onSave}
            onKeyDown={(e) => dirty && !isSaving && (e.key === "Enter" || e.key === " ") && onSave()}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              whiteSpace: "nowrap",
              flex: { xs: 1, sm: "initial" },
              gap: "8px",
              background: dirty && !isSaving ? T.primary : "#EDE0FD",
              color: dirty && !isSaving ? "#fff" : "#9E86C9",
              borderRadius: "8px",
              px: "22px",
              py: "8px",
              fontSize: 14,
              fontWeight: 700,
              fontFamily: T.fontBody,
              cursor: dirty && !isSaving ? "pointer" : "default",
              "&:focus-visible": { outline: `2px solid ${T.ink}` },
            }}
          >
            {isSaving && <CircularProgress size={14} sx={{ color: "#9E86C9" }} />}
            {isSaving ? "Saving…" : dirty ? "Save theme" : "Saved"}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default GalleryThemeStudio;
